import { useEffect, useState } from 'react';

import type { MediaWrapperContainerTypeEnum } from '@/utils/aspectRatioUtils';
import { getAspectRatioForImages } from '@/utils/aspectRatioUtils';

import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import type { ContentfulImageWithFocalPointFragment } from '@/graphql/contentful/api';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import type { CardContentMaxWidth } from '@/lib/types';

import type { CardProps } from '../../types';
import { getDeskTopMaxContentWidth } from '../BaseCardType/helpers';
import { CardContent } from '../CardContent';

import * as GS from '../../Card.styles';
import * as S from './MediaBannerType.styles';

export const MediaBannerType = (_props: CardProps) => {
  const {
    aspectRatio,
    backgroundAlignment,
    backgroundColor,
    cardGroupType,
    className,
    contentAlignment,
    contentMaxWidth,
    media,
    mediaAnimationEffect,
    mediaWrapperType,
    preload,
    sizes,
    textAlignment,
    type,
    visibleInDesktop,
    visibleInMobile,
  } = _props;

  const { deviceType } = useDeviceDetect();
  const hasBackgroundImage = !!media?.video?.url || !!media?.image?.url;

  const aspectValue = aspectRatio ??
    getAspectRatioForImages(mediaWrapperType, null, null, null, deviceType) ?? {
      x: 1,
      y: 1,
    };
  const mobileAspectValue = aspectRatio ??
    getAspectRatioForImages(mediaWrapperType, null, null, null, 'mobile') ?? {
      x: 1,
      y: 1,
    };
  const desktopAspectValue = aspectRatio ??
    getAspectRatioForImages(mediaWrapperType, null, null, null, 'desktop') ?? {
      x: 1,
      y: 1,
    };

  const deskTopContentMaxWidth = getDeskTopMaxContentWidth(
    type as string,
    contentMaxWidth as CardContentMaxWidth,
  );
  const [isEdge, setIsEdge] = useState(false);
  const isEdgeBrowser = () => /Edg|Edge/.test(navigator.userAgent);
  useEffect(() => {
    const type = isEdgeBrowser();
    setIsEdge(type);
  }, []);
  const getItemsAlignment = (alignment: string) => {
    switch (alignment) {
      case 'top':
        return 'flex-start';
      case 'bottom':
        return 'end';
      case 'leftBottom':
        return 'end';
      case 'rightBottom':
        return 'end';
      default:
        return 'center';
    }
  };
  return (
    <S.Wrapper
      key={`${aspectValue?.x}:${aspectValue?.y}`}
      $desktopAspectRatio={desktopAspectValue}
      $hasBackgroundImage={hasBackgroundImage}
      $mediaWrapperType={mediaWrapperType}
      $mobileAspectRatio={mobileAspectValue}
      className={`MediaBanner ${className}`}
      contentAlignment={contentAlignment}
      data-test-id={`${type}Wrapper`}
      mediaAnimationEffect={
        mediaAnimationEffect === 'zoomOnHover' ? mediaAnimationEffect : null
      }
      visibleInDesktop={visibleInDesktop}
      visibleInMobile={visibleInMobile}
    >
      {backgroundColor?.value && (
        <GS.Background $backgroundColor={backgroundColor?.value} />
      )}
      <GS.Media
        $backgroundAlignment={backgroundAlignment}
        data-test-id='MediaBannerMedia'
      >
        <MediaWrapper
          {...(media as ContentfulImageWithFocalPointFragment)}
          aspectRatio={aspectValue}
          cardGroupType={cardGroupType as MediaWrapperContainerTypeEnum}
          mediaWrapperType={mediaWrapperType}
          preload={preload}
          sizes={sizes}
        />
      </GS.Media>

      <S.Content
        $deskTopContentMaxWidth={deskTopContentMaxWidth}
        $isEdgeBrowser={isEdge}
        $mediaWrapperType={mediaWrapperType}
        itemsAlignment={getItemsAlignment(contentAlignment ?? 'center')}
        textAlignment={textAlignment}
      >
        <CardContent {..._props} />
      </S.Content>
    </S.Wrapper>
  );
};
